/**
 * Display-only technician score normalization.
 *
 * A finite number in [0, 100] is rounded to the nearest integer once
 * (JavaScript half-up). That same integer drives the displayed value,
 * BAD / OK / GOOD classification, and arc length. This is not written
 * back to source data.
 *
 * null, undefined, NaN, Infinity, non-numbers (including numeric
 * strings), and values outside 0–100 are unavailable. Zero is a valid
 * BAD score, not a missing value.
 */

export const SCORE_STATUS = {
  BAD: 'BAD',
  OK: 'OK',
  GOOD: 'GOOD',
};

export const SCORE_COLORS = {
  BAD: {
    arc: '#DC2626',
    tint: '#FEF2F2',
    statusText: '#991B1B',
  },
  OK: {
    arc: '#F59E0B',
    tint: '#FFFBEB',
    statusText: '#92400E',
  },
  GOOD: {
    arc: '#16A34A',
    tint: '#F0FDF4',
    statusText: '#166534',
  },
  unavailable: {
    arc: '#D1D5DB',
    tint: '#F3F4F6',
    statusText: '#6B7280',
  },
};

export const TRACK_COLOR = '#D1D5DB';

const COMPACT_SIZE = 44;
const EXPANDED_SIZE = 92;
const COMPACT_STROKE = 4;
const COMPACT_PAD = 2;

export function normalizeTechnicianScore(score) {
  if (typeof score !== 'number' || !Number.isFinite(score)) return null;
  if (score < 0 || score > 100) return null;
  return Math.round(score);
}

export function classifyTechnicianScore(normalized) {
  if (normalized === null || normalized === undefined) return null;
  if (normalized <= 49) return SCORE_STATUS.BAD;
  if (normalized <= 74) return SCORE_STATUS.OK;
  return SCORE_STATUS.GOOD;
}

export function gaugeGeometry(size = COMPACT_SIZE) {
  const strokeWidth = size * (COMPACT_STROKE / COMPACT_SIZE);
  const pad = size * (COMPACT_PAD / COMPACT_SIZE);
  const cx = size / 2;
  const r = cx - strokeWidth / 2 - pad;
  const circumference = 2 * Math.PI * r;
  return { size, strokeWidth, pad, cx, cy: cx, r, circumference };
}

export function defaultGaugeSize(variant) {
  return variant === 'expanded' ? EXPANDED_SIZE : COMPACT_SIZE;
}

export function technicianScoreView(score, { labelPrefix = 'Technician score' } = {}) {
  const normalized = normalizeTechnicianScore(score);
  if (normalized === null) {
    const colors = SCORE_COLORS.unavailable;
    return {
      available: false,
      value: null,
      display: '--',
      status: null,
      statusLabel: 'Not scored',
      fraction: 0,
      colors,
      tooltip: 'Not scored',
      ariaLabel: `${labelPrefix}: Not scored`,
    };
  }

  const status = classifyTechnicianScore(normalized);
  const colors = SCORE_COLORS[status];
  return {
    available: true,
    value: normalized,
    display: String(normalized),
    status,
    statusLabel: status,
    fraction: normalized / 100,
    colors,
    tooltip: `${normalized} / 100 - ${status}`,
    ariaLabel: `${labelPrefix}: ${normalized} out of 100, ${status}`,
  };
}
